import React, { useEffect, useState } from "react";
import Compressor from "compressorjs";
import Camera from "react-html5-camera-photo";
import "react-html5-camera-photo/build/css/index.css";
import { useThemeMode } from "../../../../_metronic/partials";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import { generateMultiSignedUrls } from "../../../models/_publicCall";
import { FOLDER_NAME } from "../../../../_metronic/helpers/enums";
import toast from "react-hot-toast";
import { isHeic, heicTo } from "heic-to";
interface FileUploadProps {
  jobId: any;
  values: any;
  setFieldValue: (field: string, value: any) => void;
  fieldName: string;
  label?: string;
  jobStatus: string;
  installationJobDB: any;
  showMandatory?: boolean;
}

const FileUpload: React.FC<FileUploadProps> = ({
  values,
  setFieldValue,
  fieldName,
  label,
  jobStatus,
  installationJobDB,
  showMandatory,
}) => {
  const [files, setFiles] = useState<
    { type: string; content: string; name: string }[]
  >([]);
  const [showCapture, setShowCapture] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"user" | "environment">("user");
  const { mode } = useThemeMode();
  const [isUploading, setIsUploading] = useState(false);

  const fieldNameToDBKey: Record<string, string> = {
    fullSetup: "imageOfFullSmoodiSetUp",
    freezerOpen: "setupFreezerDoorPhoto",
    backOfMachine: "machineBackPhoto",
    relativeToEntrance: "machineEntranceViewPhoto",
  };

  // 🔹 Load saved files from localStorage
  // 🔹 Load saved files from localStorage or installationJobDB
  useEffect(() => {
    const storedData = JSON.parse(
      localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
    );

    // Get files from localStorage
    let savedFiles = storedData.locationNotes?.[fieldName] || [];

    // Only prefill from DB if localStorage is empty
    if (
      jobStatus !== "Completed" &&
      installationJobDB &&
      (!savedFiles || savedFiles.length === 0)
    ) {
      const dbKey = fieldNameToDBKey[fieldName];
      const dbFiles = installationJobDB.uploadImagesOrVideos?.[dbKey] || [];
      if (Array.isArray(dbFiles) && dbFiles.length > 0) {
        savedFiles = dbFiles;
      }
    }

    setFiles(savedFiles);

    // Sync to Formik + localStorage
    setFieldValue(fieldName, savedFiles);
    try {
      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify({
          ...storedData,
          locationNotes: {
            ...storedData.locationNotes,
            [fieldName]: savedFiles,
          },
        }),
      );
    } catch (error) {
      console.error("Failed to save to localStorage (quota exceeded):", error);
    }
  }, [fieldName, installationJobDB, jobStatus, setFieldValue]);

  // 🔹 Initialize Formik field
  useEffect(() => {
    if (!Array.isArray(values[fieldName])) {
      setFieldValue(fieldName, []);
    }
  }, [values, fieldName, setFieldValue]);

  // 🔹 Update Formik field and localStorage whenever files change
  useEffect(() => {
    setFieldValue(fieldName, files);

    try {
      const storedData = JSON.parse(
        localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
      );
      const updatedData = {
        ...storedData,
        locationNotes: {
          ...storedData.locationNotes,
          [fieldName]: files,
        },
      };
      localStorage.setItem(
        INSTALLATION_JOB_STORAGE_KEY,
        JSON.stringify(updatedData),
      );
    } catch (error) {
      console.error("Failed to save to localStorage (quota exceeded):", error);
      toast.error(
        "Storage quota exceeded. Please reduce the number of images or clear some data.",
      );
    }
  }, [files, fieldName, setFieldValue]);

  // Sync helper for quick file extension/MIME type check (for filtering)
  const looksLikeHeicFile = (file: File): boolean => {
    return (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      file.name.toLowerCase().endsWith(".heic") ||
      file.name.toLowerCase().endsWith(".heif")
    );
  };

  // Async check using the library's isHeic function (for actual conversion)
  const isHeicFile = async (file: File): Promise<boolean> => {
    // Use the library's isHeic function first
    try {
      const result = await isHeic(file);
      if (result) return true;
    } catch (e) {
      // Fall back to file extension/type check if library check fails
    }
    // Fallback: check file extension and MIME type
    return looksLikeHeicFile(file);
  };

  const convertHeicToJpeg = async (file: File): Promise<File> => {
    try {
      // Convert HEIC to JPEG using heic-to
      const jpegBlob = await heicTo({
        blob: file,
        type: "image/jpeg",
        quality: 1.0,
      });

      if (!jpegBlob || jpegBlob.size === 0) {
        throw new Error("HEIC conversion returned empty result");
      }

      // Create a File from the JPEG Blob
      const convertedFile = new File(
        [jpegBlob],
        file.name.replace(/\.(heic|heif)$/i, ".jpg"),
        {
          type: "image/jpeg",
          lastModified: Date.now(),
        },
      );

      return convertedFile;
    } catch (error: any) {
      console.error("HEIC conversion error:", error);
      throw new Error(
        `Failed to convert HEIC file: ${error?.message || error}`,
      );
    }
  };
  const compressImage = (file: File): Promise<Blob> =>
    new Promise((resolve, reject) => {
      new Compressor(file, {
        quality: 0.4, // Reduced from 0.7 to save space
        maxWidth: 1920,
        maxHeight: 1920,
        convertSize: 1000000,
        mimeType: "image/jpeg",
        success(result) {
          resolve(result);
        },
        error(err) {
          reject(err);
        },
      });
    });

  const getBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const requestSignedUrl = async (name: string, type: string) => {
    const items = await generateMultiSignedUrls([
      {
        file_name: name,
        file_type: type,
        folder_name: FOLDER_NAME.INSTALLATION_JOB_MEDIA,
      },
    ]);
    return items?.[0]?.url || "";
  };

  const uploadToSignedUrl = async (
    signedUrl: string,
    fileOrBlob: Blob,
    type: string,
  ) => {
    const res = await fetch(signedUrl, {
      method: "PUT",
      headers: { "Content-Type": type },
      body: fileOrBlob,
    });
    if (!res.ok) throw new Error("Upload failed");
    return signedUrl.split("?")[0];
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const input = event.currentTarget;
    const fileList = input.files;
    if (!fileList) return;

    const imageFiles = Array.from(fileList).filter(
      (file) => file.type.startsWith("image/") || looksLikeHeicFile(file),
    );
    if (imageFiles.length === 0) {
      toast.error("Please select image files only.");
      input.value = "";
      return;
    }

    setIsUploading(true);

    try {
      // Compress all images first
      const compressedFiles: File[] = [];
      for (const file of imageFiles) {
        try {
          let outFile = file;
          const fileIsHeic = await isHeicFile(file);
          if (file.type.startsWith("image/") || fileIsHeic) {
            if (fileIsHeic) {
              outFile = await convertHeicToJpeg(file);
            }
            const compressedBlob = await compressImage(outFile);
            outFile = new File([compressedBlob], outFile.name, {
              type: "image/jpeg",
            });
          }
          compressedFiles.push(outFile);
        } catch (error) {
          console.error(`Error compressing ${file.name}:`, error);
          toast.error(`Failed to compress ${file.name}. Please try again.`);
        }
      }

      if (compressedFiles.length === 0) {
        input.value = "";
        return;
      }

      const existingNames = new Set(files.map((f) => f.name));
      const uniqueFiles: File[] = [];
      const skippedDuplicateNames: string[] = [];
      for (const file of compressedFiles) {
        if (
          existingNames.has(file.name) ||
          uniqueFiles.some((f) => f.name === file.name)
        ) {
          skippedDuplicateNames.push(file.name);
          continue;
        }
        uniqueFiles.push(file);
        existingNames.add(file.name);
      }

      if (uniqueFiles.length === 0) {
        if (skippedDuplicateNames.length > 0) {
          toast.error(
            `Skipped duplicate image(s): ${Array.from(
              new Set(skippedDuplicateNames),
            ).join(", ")}`,
          );
        }
        input.value = "";
        return;
      }

      // Get all signed URLs in a single API call only for unique files
      const signedUrlRequests = uniqueFiles.map((file) => ({
        file_name: file.name,
        file_type: file.type,
        folder_name: FOLDER_NAME.INSTALLATION_JOB_MEDIA,
      }));

      const signedUrls = await generateMultiSignedUrls(signedUrlRequests);

      for (let i = 0; i < uniqueFiles.length; i++) {
        const file = uniqueFiles[i];
        const signedUrl = signedUrls?.[i]?.url;

        if (!signedUrl) {
          console.error(`No signed URL returned for ${file.name}`);
          toast.error(
            `Failed to get upload URL for ${file.name}. Please try again.`,
          );
          continue;
        }

        try {
          const publicUrl = await uploadToSignedUrl(signedUrl, file, file.type);
          setFiles((prev) => [
            ...prev,
            {
              type: file.type,
              content: publicUrl,
              name: file.name,
            },
          ]);
        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          toast.error(`Failed to upload ${file.name}. Please try again.`);
        }
      }

      if (skippedDuplicateNames.length > 0) {
        toast.error(
          `Skipped duplicate image(s): ${Array.from(
            new Set(skippedDuplicateNames),
          ).join(", ")}`,
        );
      }
    } finally {
      setIsUploading(false);
      input.value = "";
    }
  };

  const handleRemoveFile = (fileContent: string) => {
    setFiles((prev) => {
      const newFiles = prev.filter((file) => file.content !== fileContent);

      // Immediately update Formik values to avoid timing issues
      setFieldValue(fieldName, newFiles);

      return newFiles;
    });
  };

  const handleTakePhoto = async (dataUri: string) => {
    const fileName = `captured_image_${Date.now()}.jpg`;
    const file = dataURLtoFile(dataUri, fileName);

    try {
      setIsUploading(true);
      const compressedBlob = await compressImage(file);
      const compressedFile = new File([compressedBlob], file.name, {
        type: "image/jpeg",
      });
      const signedUrl = await requestSignedUrl(
        compressedFile.name,
        compressedFile.type,
      );
      if (!signedUrl) throw new Error("No signed URL returned");
      const publicUrl = await uploadToSignedUrl(
        signedUrl,
        compressedFile,
        compressedFile.type,
      );
      setFiles((prev) => [
        ...prev,
        {
          type: compressedFile.type,
          content: publicUrl,
          name: compressedFile.name,
        },
      ]);
    } catch (error) {
      console.error("Error compressing/uploading captured image:", error);
      toast.error("Failed to upload captured image. Please try again.");
    } finally {
      setIsUploading(false);
    }

    setShowCapture(false);
    setCameraError(null);
  };

  const handleCameraError = (error: any) => {
    setCameraError(
      "Unable to access the camera. Please check browser permissions or settings.",
    );
  };

  const toggleFacingMode = () => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
  };

  return (
    <div className="wo-form-section mt-8">
      <div className="upload-section">
        {label && (
          <h3>
            {label} {showMandatory && <span className="text-danger">*</span>}
          </h3>
        )}
        <div
          className={`upload-box ${mode === "dark" ? "dark-upload-box" : ""}`}
        >
          <input
            type="file"
            className="file-input"
            multiple
            accept=".png, .heic, .heif, .jpg, .jpeg, .webp"
            onChange={handleFileChange}
          />

          {files.length > 0 ? (
            <div className="preview-container d-flex flex-wrap mt-3">
              {files.map((file, index) => (
                <div
                  key={index}
                  className="img-box mt-3 ms-2 justify-content-center"
                >
                  {file.type.startsWith("image/") ? (
                    <img
                      src={file.content}
                      width="150"
                      className="img-wrapper overflow-hidden m-2 ms-0 d-flex position-relative"
                    />
                  ) : (
                    <video
                      controls
                      src={file.content}
                      width="170"
                      className="img-wrapper overflow-hidden m-2 ms-0 d-flex position-relative"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(file.content)}
                    className="close-btn img-btn"
                  >
                    <i className="fa fa-close fs-5 p-1"></i>
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="file-label text-center p-3">
              <i className="fa fa-cloud-upload cloud-icon fs-3"></i>
              <span className="d-block mt-2">Browse Files</span>
              <p className="text-muted">Drag and drop files here</p>
            </div>
          )}
          {isUploading && (
            <div className="d-flex align-items-center mt-2">
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              <span>Uploading...</span>
            </div>
          )}
        </div>

        {/* Camera Capture */}
        <div className="mt-3">
          {!showCapture && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowCapture(true)}
            >
              Capture Image
            </button>
          )}

          {showCapture && (
            <>
              {cameraError && (
                <div className="alert alert-danger mt-2" role="alert">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  {cameraError}
                </div>
              )}

              <Camera
                onTakePhoto={handleTakePhoto}
                onCameraError={(err) => {
                  console.log(err);
                  setCameraError("Camera error");
                }}
                idealFacingMode={facingMode}
                isFullscreen={false}
                imageType="png"
              />

              <div className="mt-3 d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={toggleFacingMode}
                >
                  Switch to {facingMode === "user" ? "Back" : "Front"} Camera
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => {
                    setShowCapture(false);
                    setCameraError(null);
                  }}
                >
                  Dismiss
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const dataURLtoFile = (dataUrl: string, fileName: string): File => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], fileName, { type: mime });
};

export default FileUpload;
